import React, {useCallback, useEffect, useState} from 'react';
import {StyleSheet, css} from 'aphrodite';
import {useNavigate} from 'react-router-dom';
import {FiShoppingBag} from 'react-icons/fi';

import api from '../../api/api';
import OutletCard from '../OutletCard';

const AllOutlets = ({selectedTab = 'Delivery'}) => {
  const [outlets, setOutlets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const navigate = useNavigate();

  const fetchOutlets = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage('');

      const response = await api.get('/restaurants', {params: {isAvailable: true}});

      const fetchedOutlets = response?.data?.data || response?.data || [];

      const openOutlets = Array.isArray(fetchedOutlets)
        ? fetchedOutlets.filter(outlet => Boolean(outlet?.isOpen && outlet?.isAvailable))
        : [];

      setOutlets(openOutlets);
    } catch (error) {
      console.error('Error fetching outlets:', error);

      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          'Failed to load outlets. Please try again.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOutlets();
  }, [fetchOutlets]);

  const handleOutletPress = outlet => {
    navigate(`/restaurant/${outlet.id}`, {
      state: {
        outlet,
        selectedTab,
      },
    });
  };

  if (loading) {
    return (
      <div className={css(styles.stateCard)}>
        <div className={css(styles.loaderBadge)}>YB</div>

        <p className={css(styles.stateTitle)}>Loading outlets</p>

        <p className={css(styles.stateText)}>
          Please wait while we fetch available restaurants.
        </p>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className={css(styles.stateCard)}>
        <div className={css(styles.errorBadge)}>!</div>

        <p className={css(styles.stateTitle)}>Could not load outlets</p>

        <p className={css(styles.stateText)}>{errorMessage}</p>

        <button
          type="button"
          className={css(styles.retryButton)}
          onClick={fetchOutlets}>
          Try Again
        </button>
      </div>
    );
  }

  if (!outlets.length) {
    return (
      <div className={css(styles.stateCard)}>
        <div className={css(styles.emptyBadge)}><FiShoppingBag /></div>

        <p className={css(styles.stateTitle)}>No outlets available</p>

        <p className={css(styles.stateText)}>
          There are no outlets available right now. Please check again soon.
        </p>
      </div>
    );
  }

  return (
    <section className={css(styles.container)}>
      <div className={css(styles.outletsGrid)}>
        {outlets.map(outlet => (
          <div key={outlet.id || outlet._id} className={css(styles.cardWrap)}>
            <OutletCard
              outlet={outlet}
              selectedTab={selectedTab}
              onClick={() => handleOutletPress(outlet)}
            />
          </div>
        ))}
      </div>
    </section>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    boxSizing: 'border-box',
  },

  outletsGrid: {
    width: '100%',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
    gap: '22px',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    boxSizing: 'border-box',
  },

  cardWrap: {
    width: '100%',
    boxSizing: 'border-box',
  },

  stateCard: {
    width: '100%',
    maxWidth: '560px',
    minHeight: '250px',
    backgroundColor: '#FFFDF7',
    border: '1px solid #E6D8BE',
    borderRadius: '28px',
    padding: '34px 24px',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
  },

  loaderBadge: {
    width: '66px',
    height: '66px',
    borderRadius: '50%',
    backgroundColor: '#0F3D2E',
    border: '2px solid #C9A14A',
    color: '#C9A14A',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '21px',
    fontWeight: '900',
    marginBottom: '16px',
  },

  emptyBadge: {
    width: '66px',
    height: '66px',
    borderRadius: '50%',
    backgroundColor: '#0F3D2E',
    border: '2px solid #C9A14A',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '26px',
    marginBottom: '16px',
  },

  errorBadge: {
    width: '66px',
    height: '66px',
    borderRadius: '50%',
    backgroundColor: '#FFE9E9',
    border: '2px solid #F5B8B8',
    color: '#D9534F',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    fontWeight: '900',
    marginBottom: '16px',
  },

  stateTitle: {
    margin: '0 0 8px',
    color: '#0F3D2E',
    fontSize: '20px',
    fontWeight: '900',
    letterSpacing: '-0.4px',
  },

  stateText: {
    margin: 0,
    maxWidth: '420px',
    color: '#6E6254',
    fontSize: '14px',
    lineHeight: 1.7,
  },

  retryButton: {
    marginTop: '18px',
    padding: '12px 18px',
    borderRadius: '18px',
    border: 'none',
    backgroundColor: '#C9A14A',
    color: '#0F3D2E',
    fontSize: '14px',
    fontWeight: '900',
    cursor: 'pointer',
    boxShadow: '0 10px 24px rgba(201, 161, 74, 0.18)',

    ':hover': {
      transform: 'translateY(-1px)',
    },
  },
});

export default AllOutlets;
